import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
    AlertCircle,
    CheckCircle2,
    Clock,
    Inbox,
    Loader,
    MailPlus,
    RefreshCw,
    Send,
    ShieldCheck,
    Trash2,
    UserCheck,
    UserX
} from 'lucide-react';
import { adminAPI } from '../../utils/api.js';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal.jsx';
import './InvitationsTab.scss';

const ROLE_OPTIONS = [
    { value: 'technician', label: 'Technician' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Admin' }
];

const formatDate = (value) => {
    if (!value) return 'Not yet';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown';

    return new Intl.DateTimeFormat('en', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    }).format(date);
};

const getStatusLabel = (status) => (
    String(status || 'pending')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
);

const isPending = (status) => ['pending', 'requested'].includes(String(status).toLowerCase());

const InvitationsTab = () => {
    const [invitations, setInvitations] = useState([]);
    const [accessRequests, setAccessRequests] = useState([]);
    const [requestRoles, setRequestRoles] = useState({});
    const [formData, setFormData] = useState({ email: '', role: 'technician' });
    const [loading, setLoading] = useState(true);
    const [actionKey, setActionKey] = useState('');
    const [notice, setNotice] = useState(null);
    const [invitationToDelete, setInvitationToDelete] = useState(null);
    const invitationsHeading = useRef(null);

    const loadData = async ({ showLoading = true } = {}) => {
        if (showLoading) setLoading(true);

        try {
            const [invitationResponse, requestResponse] = await Promise.all([
                adminAPI.getInvitations(),
                adminAPI.getAccessRequests()
            ]);

            const nextInvitations = invitationResponse.data?.invitations || [];
            const nextRequests = requestResponse.data?.accessRequests || [];
            setInvitations(nextInvitations);
            setAccessRequests(nextRequests);
            setRequestRoles((currentRoles) => {
                const nextRoles = {};
                nextRequests.forEach((request) => {
                    nextRoles[request.id] = currentRoles[request.id] || 'technician';
                });
                return nextRoles;
            });
        } catch (loadError) {
            setNotice({
                type: 'error',
                message: loadError.message || 'Unable to load invitations and access requests.'
            });
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const runAction = async (key, action, getMessage, { rethrow = false } = {}) => {
        setActionKey(key);
        setNotice(null);

        try {
            const result = await action();
            setNotice({ type: 'success', message: getMessage(result) });
            await loadData({ showLoading: false });
            return result;
        } catch (actionError) {
            if (rethrow) throw actionError;
            setNotice({
                type: 'error',
                message: actionError.message || 'The action could not be completed.'
            });
            return null;
        } finally {
            setActionKey('');
        }
    };

    const handleCreateInvitation = async (event) => {
        event.preventDefault();
        const email = formData.email.trim();

        if (!email) {
            setNotice({ type: 'error', message: 'Enter an email address to create an invitation.' });
            return;
        }

        const result = await runAction(
            'create',
            () => adminAPI.createInvitation({ email, role: formData.role }),
            (response) => response.data?.emailSent === false
                ? `Invitation created for ${email}, but the email could not be sent.`
                : `Invitation sent to ${email}.`
        );

        if (result) {
            setFormData((current) => ({ ...current, email: '' }));
        }
    };

    const handleResend = (invitation) => {
        runAction(
            `resend:${invitation.id}`,
            () => adminAPI.resendInvitation(invitation.id),
            (response) => response.data?.emailSent === false
                ? `The invitation remains pending, but the email to ${invitation.email} could not be sent.`
                : `Invitation resent to ${invitation.email}.`
        );
    };

    const handleDelete = async () => {
        if (!invitationToDelete) return;
        const invitation = invitationToDelete;
        await runAction(
            `delete:${invitation.id}`,
            () => adminAPI.deleteInvitation(invitation.id),
            () => `Invitation for ${invitation.email} deleted.`,
            { rethrow: true }
        );
    };

    const handleApprove = (request) => {
        const role = requestRoles[request.id] || 'technician';
        runAction(
            `approve:${request.id}`,
            () => adminAPI.approveAccessRequest(request.id, role),
            (response) => response.data?.emailSent === false
                ? `${request.email} was approved as ${role}, but the invitation email could not be sent.`
                : `${request.email} approved as ${role} and invited.`
        );
    };

    const handleDeny = (request) => {
        runAction(
            `deny:${request.id}`,
            () => adminAPI.denyAccessRequest(request.id),
            () => `Access request from ${request.email} denied.`
        );
    };

    const pendingInvitationCount = invitations.filter((invitation) => isPending(invitation.status)).length;
    const pendingRequestCount = accessRequests.filter((request) => isPending(request.status)).length;

    if (loading) {
        return (
            <div className="invitations-tab invitations-tab--loading">
                <Loader className="invitations-tab__spinner" size={38} />
                <p>Loading invitation access...</p>
            </div>
        );
    }

    return (
        <div className="invitations-tab">
            <ConfirmationModal
                isOpen={Boolean(invitationToDelete)}
                onCancel={() => setInvitationToDelete(null)}
                onConfirm={handleDelete}
                title="Delete invitation?"
                message={`Delete the invitation for ${invitationToDelete?.email || ''}?`}
                confirmText="Delete invitation"
                pendingText="Deleting..."
                variant="danger"
                returnFocusRef={invitationsHeading}
            />
            <motion.div
                className="invitations-tab__hero"
                initial={{ opacity: 0, y: -14 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <div className="invitations-tab__hero-copy">
                    <div className="invitations-tab__hero-icon">
                        <MailPlus />
                    </div>
                    <div>
                        <h1>Invitations & Access</h1>
                        <p>Invite Google accounts and review requests for the current location.</p>
                    </div>
                </div>
                <button
                    type="button"
                    className="invitations-tab__refresh"
                    onClick={() => loadData()}
                    disabled={Boolean(actionKey)}
                >
                    <RefreshCw size={17} />
                    Refresh
                </button>
            </motion.div>

            {notice && (
                <div className={`invitations-tab__notice invitations-tab__notice--${notice.type}`} role="status">
                    {notice.type === 'success' ? <CheckCircle2 size={19} /> : <AlertCircle size={19} />}
                    <span>{notice.message}</span>
                    <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss message">×</button>
                </div>
            )}

            <div className="invitations-tab__summary">
                <div className="invitations-tab__summary-card">
                    <Send size={21} />
                    <div>
                        <strong>{pendingInvitationCount}</strong>
                        <span>Pending invitations</span>
                    </div>
                </div>
                <div className="invitations-tab__summary-card">
                    <Inbox size={21} />
                    <div>
                        <strong>{pendingRequestCount}</strong>
                        <span>Requests to review</span>
                    </div>
                </div>
            </div>

            <motion.section
                className="invitations-tab__panel invitations-tab__create"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
            >
                <div className="invitations-tab__section-heading">
                    <div>
                        <h2>Create invitation</h2>
                        <p>The recipient must sign in with this exact Google email address.</p>
                    </div>
                    <ShieldCheck size={22} />
                </div>

                <form className="invitations-tab__invite-form" onSubmit={handleCreateInvitation}>
                    <label>
                        <span>Email address</span>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(event) => setFormData((current) => ({
                                ...current,
                                email: event.target.value
                            }))}
                            placeholder="person@company.com"
                            autoComplete="email"
                            disabled={actionKey === 'create'}
                            required
                        />
                    </label>
                    <label>
                        <span>Role</span>
                        <select
                            value={formData.role}
                            onChange={(event) => setFormData((current) => ({
                                ...current,
                                role: event.target.value
                            }))}
                            disabled={actionKey === 'create'}
                        >
                            {ROLE_OPTIONS.map((role) => (
                                <option key={role.value} value={role.value}>{role.label}</option>
                            ))}
                        </select>
                    </label>
                    <button type="submit" disabled={actionKey === 'create'}>
                        {actionKey === 'create' ? (
                            <><Loader className="invitations-tab__spinner" size={17} /> Sending...</>
                        ) : (
                            <><Send size={17} /> Send invitation</>
                        )}
                    </button>
                </form>
            </motion.section>

            <motion.section
                className="invitations-tab__panel"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
            >
                <div className="invitations-tab__section-heading">
                    <div>
                        <h2 ref={invitationsHeading} tabIndex={-1}>Invitations</h2>
                        <p>Track delivery, acceptance, and the account created from each invite.</p>
                    </div>
                    <span className="invitations-tab__count">{invitations.length}</span>
                </div>

                {invitations.length === 0 ? (
                    <div className="invitations-tab__empty">
                        <MailPlus size={30} />
                        <strong>No invitations yet</strong>
                        <span>Create the first invitation above.</span>
                    </div>
                ) : (
                    <div className="invitations-tab__list">
                        {invitations.map((invitation) => {
                            const pending = isPending(invitation.status);
                            const resendKey = `resend:${invitation.id}`;
                            const deleteKey = `delete:${invitation.id}`;

                            return (
                                <article className="invitations-tab__row" key={invitation.id}>
                                    <div className="invitations-tab__identity">
                                        <strong>{invitation.email}</strong>
                                        {invitation.user ? (
                                            <span>
                                                {invitation.user.name || 'Mapped user'}
                                                {invitation.user.ejentoUserId ? ` · ${invitation.user.ejentoUserId}` : ''}
                                            </span>
                                        ) : <span>Awaiting acceptance</span>}
                                    </div>
                                    <div className="invitations-tab__metadata">
                                        <span className={`invitations-tab__status invitations-tab__status--${String(invitation.status || 'pending').toLowerCase()}`}>
                                            {getStatusLabel(invitation.status)}
                                        </span>
                                        <span className="invitations-tab__role">{getStatusLabel(invitation.role)}</span>
                                    </div>
                                    <div className="invitations-tab__date">
                                        <Clock size={15} />
                                        <span>
                                            {invitation.acceptedAt
                                                ? `Accepted ${formatDate(invitation.acceptedAt)}`
                                                : invitation.lastSentAt
                                                    ? `Sent ${formatDate(invitation.lastSentAt)}`
                                                    : `Created ${formatDate(invitation.createdAt)}`}
                                        </span>
                                    </div>
                                    <div className="invitations-tab__actions">
                                        {pending && (
                                            <button
                                                type="button"
                                                className="invitations-tab__action invitations-tab__action--secondary"
                                                onClick={() => handleResend(invitation)}
                                                disabled={Boolean(actionKey)}
                                            >
                                                {actionKey === resendKey
                                                    ? <Loader className="invitations-tab__spinner" size={16} />
                                                    : <RefreshCw size={16} />}
                                                Resend
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="invitations-tab__action invitations-tab__action--danger"
                                            onClick={() => setInvitationToDelete(invitation)}
                                            disabled={Boolean(actionKey)}
                                            aria-label={`Delete invitation for ${invitation.email}`}
                                        >
                                            {actionKey === deleteKey
                                                ? <Loader className="invitations-tab__spinner" size={16} />
                                                : <Trash2 size={16} />}
                                            Delete
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </motion.section>

            <motion.section
                className="invitations-tab__panel"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
            >
                <div className="invitations-tab__section-heading">
                    <div>
                        <h2>Access requests</h2>
                        <p>Approve a role and send an invitation, or deny the request.</p>
                    </div>
                    <span className="invitations-tab__count">{accessRequests.length}</span>
                </div>

                {accessRequests.length === 0 ? (
                    <div className="invitations-tab__empty">
                        <Inbox size={30} />
                        <strong>No access requests</strong>
                        <span>New requests will appear here for review.</span>
                    </div>
                ) : (
                    <div className="invitations-tab__list">
                        {accessRequests.map((request) => {
                            const pending = isPending(request.status);
                            const approveKey = `approve:${request.id}`;
                            const denyKey = `deny:${request.id}`;

                            return (
                                <article className="invitations-tab__row invitations-tab__row--request" key={request.id}>
                                    <div className="invitations-tab__identity">
                                        <strong>{request.displayName || request.email}</strong>
                                        <span>{request.email}</span>
                                    </div>
                                    <div className="invitations-tab__metadata">
                                        <span className={`invitations-tab__status invitations-tab__status--${String(request.status || 'pending').toLowerCase()}`}>
                                            {getStatusLabel(request.status)}
                                        </span>
                                    </div>
                                    <div className="invitations-tab__date">
                                        <Clock size={15} />
                                        <span>
                                            {request.reviewedAt
                                                ? `Reviewed ${formatDate(request.reviewedAt)}`
                                                : `Requested ${formatDate(request.requestedAt)}`}
                                        </span>
                                    </div>
                                    <div className="invitations-tab__actions invitations-tab__actions--request">
                                        {pending ? (
                                            <>
                                                <select
                                                    value={requestRoles[request.id] || 'technician'}
                                                    onChange={(event) => setRequestRoles((current) => ({
                                                        ...current,
                                                        [request.id]: event.target.value
                                                    }))}
                                                    disabled={Boolean(actionKey)}
                                                    aria-label={`Role for ${request.email}`}
                                                >
                                                    {ROLE_OPTIONS.map((role) => (
                                                        <option key={role.value} value={role.value}>{role.label}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button"
                                                    className="invitations-tab__action invitations-tab__action--approve"
                                                    onClick={() => handleApprove(request)}
                                                    disabled={Boolean(actionKey)}
                                                >
                                                    {actionKey === approveKey
                                                        ? <Loader className="invitations-tab__spinner" size={16} />
                                                        : <UserCheck size={16} />}
                                                    Approve
                                                </button>
                                                <button
                                                    type="button"
                                                    className="invitations-tab__action invitations-tab__action--danger"
                                                    onClick={() => handleDeny(request)}
                                                    disabled={Boolean(actionKey)}
                                                >
                                                    {actionKey === denyKey
                                                        ? <Loader className="invitations-tab__spinner" size={16} />
                                                        : <UserX size={16} />}
                                                    Deny
                                                </button>
                                            </>
                                        ) : (
                                            <span className="invitations-tab__reviewed">
                                                <CheckCircle2 size={16} /> Review complete
                                            </span>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </motion.section>
        </div>
    );
};

export default InvitationsTab;
